import React from 'react';
import { Modal, Table, OverlayTrigger, Tooltip, Col } from 'react-bootstrap';
import moment from 'moment';

//elements
import Button from '../../../../components/CustomButton/CustomButton';
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

//styles
import '../../../../styles/css/timelogs.css';

const CycleTable = ({ cycles, t }) => (
    <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
        <Table className="table-sm fs12 table-container mb-0">
            <thead>
                <tr>
                    <th className="text-start" style={{ width: '2%' }}>#</th>
                    <th className="text-start" style={{ width: '23%' }}>{t('Employee')}</th>
                    <th className="text-start" style={{ width: '30%' }}>{t('Existing')} {t('Template')}</th>
                    <th className="text-start" style={{ width: '20%' }}>{t('Start Date')}</th>
                    <th className="text-start" style={{ width: '15%' }}>{t('End Date')}</th>
                    <th className="text-end" style={{ width: '10%' }}>{t('Status')}</th>
                </tr>
            </thead>
            <tbody>
                {cycles.map((c, i) => (
                    <tr key={i}>
                        <td className="text-start">{i + 1}</td>
                        <td className="text-start">{c.name}</td>
                        <td className="text-start">{c.work_pattern_name ? c.work_pattern_name : c.template}</td>
                        <td className="text-start">{c.cycle_start_date}</td>
                        <td className="text-start">{c.cycle_end_date}</td>
                        <td className="text-end">{c.cycle_status ? <span className="text-dark">{t(c.cycle_status)}</span> : <span>-</span>}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    </div>
);

const ConflictCyclesModal = ({ show, onHide, onConfirm, data, confirmLabel, t }) => {
    if (!data) return null;

    const { truncations, deletions, hasPublished } = data;
    const hasBoth = truncations.length > 0 && deletions.length > 0;

    return (
        <Modal centered size="xl" show={show} onHide={onHide}>
            <Modal.Header>
                <Modal.Title className="fs14 fw-700">{t('Conflicting Cycles Detected')}</Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" alt="close icon" onClick={onHide} />
            </Modal.Header>

            <Modal.Body>
                {hasPublished && (
                    <div className="py-2 fs13 mb-3">
                        <strong className='text-warning'>{t('Warning')}:</strong> {t('Some cycles are already published. Updating this work pattern will update existing published shifts.')}
                    </div>
                )}

                <div className={hasBoth ? 'row g-3' : ''}>
                    {truncations.length > 0 && (
                        <div className={hasBoth ? 'col' : ''}>
                            <p className="fs13 fw-600 mb-2 text-primary-color">
                                {t('In-progress cycles that will be adjusted from the new start date for below users')}
                            </p>
                            <CycleTable cycles={truncations} t={t} />
                        </div>
                    )}

                    {deletions.length > 0 && (
                        <div className={hasBoth ? 'col border-start' : ''}>
                            <p className="fs13 fw-600 mb-2 text-danger">
                                {t('Future cycles that will be removed for below users')}
                            </p>
                            <CycleTable cycles={deletions} t={t} />
                        </div>
                    )}
                </div>

                <div className="mt-3 pt-2 border-top text-center">
                    <strong className="fs13">{t('Do you want to proceed and assign the new pattern?')}</strong>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button fill danger onClick={onConfirm}>{confirmLabel}</Button>
                <Button onClick={onHide}>{t('Cancel')}</Button>
            </Modal.Footer>
        </Modal>
    );
};

export const EmployeeCycleModal = ({ show, onHide, emp, t, dateFormat }) => {
    if (!emp) return null;

    const today = moment().startOf('day');
    const cycles = emp.generated_cycles || [];

    const currentCycle = cycles.find(c =>
        moment(c.start).isSameOrBefore(today, 'day') && moment(c.end).isSameOrAfter(today, 'day')
    );

    const sortedCycles = [...cycles].sort((a, b) => moment(a.start).diff(moment(b.start)));

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header>
                <Modal.Title className="fs14 fw-700">{emp.employee_name}</Modal.Title>
                <img src={CloseModalIcon} className="cursor-pointer" alt="close icon" onClick={onHide} />
            </Modal.Header>

            <Modal.Body>
                <div className="row timelog-grid-time-card mb-2">
                    <Col xs={12} md={6} className="card-timelog-grid-item d-flex align-items-center justify-content-between gap-2 py-2">
                        <span>{t('Template')}</span>
                        <span className="fw-600 fs13 text-end">{emp.template_name}</span>
                    </Col>

                    <Col xs={12} md={6} className="card-timelog-grid-item d-flex align-items-center justify-content-between gap-2 py-2">
                        <span>{t('Current Cycle')}</span>

                        {currentCycle ? (
                            <span className="fw-600 fs13 text-nowrap">{moment(currentCycle.start).format(dateFormat)} &rarr; {moment(currentCycle.end).format(dateFormat)}</span>
                        ) : (
                            <span className="text-muted fst-italic fs13 text-nowrap">{t('No active cycle')}</span>
                        )}
                    </Col>
                </div>

                <div className="fs13 fw-600">
                    {t('Generated Cycles')}
                    <OverlayTrigger placement="top" overlay={<Tooltip id="generated-cycles-info">
                        {t('The below roster period cycles were generated using the')} {emp.template_name} {t('work pattern template')}
                    </Tooltip>}>
                        <i className="fa fa-info-circle ms-1 text-muted fs12 cursor-pointer" />
                    </OverlayTrigger>
                </div>
                {sortedCycles.length > 0 ? (
                    <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                        <Table className="table-sm fs12 table-container mb-0">
                            <thead>
                                <tr>
                                    <th className="text-start" style={{ width: '8%' }}>#</th>
                                    <th className="text-center" style={{ width: '72%' }}>{t('Period')}</th>
                                    <th className="text-center" style={{ width: '20%' }}>{t('Status')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedCycles.map((c, i) => (
                                    <tr key={i}>
                                        <td className="text-start">{i + 1}</td>
                                        <td className="text-center fw-600 text-primary-color">
                                            {moment(c.start).format(dateFormat)} &rarr; {moment(c.end).format(dateFormat)}
                                        </td>
                                        <td className="text-center">
                                            {c.is_merged ? <span className="badge rounded-1 text-primary-color">{t('Shared')}</span> : <span className="text-muted">-</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <p className="text-muted fst-italic fs13">{t('No cycles generated yet')}</p>
                )}

                {emp.override_comment && (
                    <div className="mt-3 pt-2 border-top">
                        <p className="fs13 fw-600 mb-1">{t('Comment')}</p>
                        <p className="fs13 mb-0">{emp.override_comment}</p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide}>{t('Close')}</Button>
            </Modal.Footer>
        </Modal >
    );
};

export default ConflictCyclesModal;
